/**
 * Collapse legacy duplicate bond ledger rows (connection_accepted + dog_joined_play_circle).
 */

export type PlayUpdateTimelineItem = {
  id: string
  type: string
  summary: string
  createdAt: string
  readAt: string | null
  playdateId: string | null
  relationshipId?: string | null
}

/** Prefer connection_accepted; drop dog_joined_play_circle for the same relationship. */
export function dedupePlayUpdateTimelineItems<T extends PlayUpdateTimelineItem>(
  items: T[]
): T[] {
  const joinedKeys = new Set<string>()
  for (const item of items) {
    if (item.type === 'connection_accepted' && item.relationshipId) {
      joinedKeys.add(item.relationshipId)
    }
  }

  return items.filter((item) => {
    if (item.type !== 'dog_joined_play_circle') return true
    if (item.relationshipId && joinedKeys.has(item.relationshipId)) return false
    // Also drop orphan joined rows when a same-summary connection exists nearby
    return !items.some(
      (other) =>
        other.type === 'connection_accepted' &&
        other.summary === item.summary &&
        Math.abs(
          new Date(other.createdAt).getTime() - new Date(item.createdAt).getTime()
        ) < 60_000
    )
  })
}

export type PlayUpdateInvitationPresentation = {
  /** Warm CTA under the update row. */
  actionLabel: string
  /**
   * True only while the live CirclePlaydate is still proposed.
   * Agreed/terminal never show "Open invitation".
   */
  openInvitation: boolean
}

/**
 * Invitation copy must follow live playdate status, not PlayActivityEvent.type alone.
 * proposed → Open invitation
 * agreed → Playdate confirmed / View relationship
 * terminal / unknown → View relationship (never Open invitation)
 */
export function resolvePlayUpdateInvitationPresentation(
  item: { type: string; playdateId: string | null },
  liveStatusByPlaydateId: Readonly<Record<string, string | undefined>>
): PlayUpdateInvitationPresentation {
  if (item.type !== 'playdate_proposed') {
    return { actionLabel: 'View relationship', openInvitation: false }
  }
  const live =
    item.playdateId != null
      ? liveStatusByPlaydateId[item.playdateId]
      : undefined
  if (live === 'proposed') {
    return { actionLabel: 'Open invitation', openInvitation: true }
  }
  if (live === 'agreed') {
    return { actionLabel: 'Playdate confirmed', openInvitation: false }
  }
  return { actionLabel: 'View relationship', openInvitation: false }
}

export function resolvePlayUpdateNavigation(item: {
  type: string
  playdateId: string | null
  relationshipId?: string | null
}): {
  kind: 'review_playdate' | 'open_relationship' | 'none'
  relationshipId: string | null
  playdateId: string | null
} {
  const relationshipId = item.relationshipId ?? null
  const playdateId = item.playdateId ?? null

  if (
    item.type === 'playdate_proposed' &&
    (playdateId || relationshipId)
  ) {
    // Always land on the relationship; parent opens review only while status is still proposed.
    return { kind: 'open_relationship', relationshipId, playdateId }
  }

  if (
    relationshipId &&
    (item.type.startsWith('playdate_') ||
      item.type === 'connection_accepted' ||
      item.type === 'dog_joined_play_circle')
  ) {
    return { kind: 'open_relationship', relationshipId, playdateId }
  }

  if (relationshipId) {
    return { kind: 'open_relationship', relationshipId, playdateId }
  }

  return { kind: 'none', relationshipId: null, playdateId: null }
}
